import React, { useState, useEffect, useRef } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { convertImageUrl } from '../utils/convertImageUrl';

const defaultAnnouncement = {
  enabled: false,
  message: '',
  startDate: '',
  startTime: '00:00',
  endDate: '',
  endTime: '23:59',
};

const defaultPromo = {
  enabled: false,
  title: '',
  description: '',
  imageUrl: '',
  showFloating: true,
  showSection: true,
};

const defaultLeague = [
  { day: 'Mon', time: '6.30 pm - 9.30 pm', enabled: true },
  { day: 'Tue', time: '6.30 pm - 10.00 pm', enabled: true },
  { day: 'Wed', time: '6.30 pm - 9.30 pm', enabled: true },
  { day: 'Thu', time: '6.30 pm - 9.30 pm', enabled: true },
  { day: 'Fri', time: '6.30 pm - 9.30 pm', enabled: true },
  { day: 'Sat', time: '12.00 pm - 10.00 pm', enabled: true },
  { day: 'Sun', time: '', enabled: false },
];

const defaultOpen = [
  { day: 'Mon', time: '3.00 pm - 4.30 pm', enabled: true },
  { day: 'Tue', time: '3.00 pm - 4.30 pm', enabled: true },
  { day: 'Wed', time: '3.00 pm - 4.30 pm', enabled: true },
  { day: 'Thu', time: '10.00 am - 4.30 pm', enabled: true },
  { day: 'Fri', time: '3.00 pm - 10.00 pm', enabled: true, tags: 'Rock & Bowl, Reg Lanes' },
  { day: 'Sat', time: '12.00 pm - 10.00 pm', enabled: true, tags: 'Rock & Bowl, Reg Lanes' },
  { day: 'Sun', time: '12.00 pm - 10.00 pm', enabled: true },
];

function AdminPanel() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [leagueTimings, setLeagueTimings] = useState(defaultLeague);
  const [openTimings, setOpenTimings] = useState(defaultOpen);
  const [announcement, setAnnouncement] = useState(defaultAnnouncement);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [announcementSaved, setAnnouncementSaved] = useState(false);
  const [promo, setPromo] = useState(defaultPromo);
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoSaved, setPromoSaved] = useState(false);
  const [promoImages, setPromoImages] = useState([]);
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, fileName, originalSize, compressedSize }
  const [compressing, setCompressing] = useState(false);
  const [flyerType, setFlyerType] = useState('image'); // 'image' or 'pdf'
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/admin/login');
      } else {
        setUser(currentUser);
        loadSchedule();
        loadAnnouncement();
        loadPromo();
        loadPromoImages();
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadPromoImages = async () => {
    try {
      const res = await fetch('/images/promo/manifest.json');
      if (res.ok) {
        const data = await res.json();
        setPromoImages(data.images || []);
      }
    } catch (err) {
      console.error('Error loading promo manifest:', err);
    }
  };

  const loadSchedule = async () => {
    try {
      const docRef = doc(db, 'schedule', 'timings');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.league) setLeagueTimings(data.league);
        if (data.open) setOpenTimings(data.open);
      }
    } catch (err) {
      console.error('Error loading schedule:', err);
    }
  };

  const loadAnnouncement = async () => {
    try {
      const docRef = doc(db, 'schedule', 'announcement');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setAnnouncement({ ...defaultAnnouncement, ...docSnap.data() });
      }
    } catch (err) {
      console.error('Error loading announcement:', err);
    }
  };

  const loadPromo = async () => {
    try {
      const docRef = doc(db, 'schedule', 'promo');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setPromo({ ...defaultPromo, ...docSnap.data() });
      }
    } catch (err) {
      console.error('Error loading promo:', err);
    }
  };

  const handleSavePromo = async () => {
    setPromoSaving(true);
    setPromoSaved(false);
    try {
      await setDoc(doc(db, 'schedule', 'promo'), {
        ...promo,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
      });
      setPromoSaved(true);
      setTimeout(() => setPromoSaved(false), 3000);
    } catch (err) {
      alert('Error saving promo: ' + err.message);
    }
    setPromoSaving(false);
  };

  const handleRemoveImage = () => {
    setPromo({ ...promo, imageUrl: '' });
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          let { width, height } = img;

          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Try JPEG first at 0.75 quality, fall back to PNG for transparency
          let dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          if (file.type === 'image/png' && dataUrl.length > e.target.result.length) {
            dataUrl = canvas.toDataURL('image/png');
          }

          resolve({
            dataUrl,
            fileName: file.name,
            originalSize: file.size,
            compressedSize: Math.round((dataUrl.length * 3) / 4), // rough base64 decode size
            width,
            height,
          });
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderPdfFirstPage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const pdfjsLib = await import('pdfjs-dist');
          pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(e.target.result) }).promise;
          const page = await pdf.getPage(1);
          const scale = 1.5;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;

          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve({
            dataUrl,
            fileName: file.name,
            originalSize: file.size,
            compressedSize: Math.round((dataUrl.length * 3) / 4),
            width: Math.round(viewport.width),
            height: Math.round(viewport.height),
            isPdf: true,
            totalPages: pdf.numPages,
          });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
    const isPdf = file.type === 'application/pdf';

    if (!imageTypes.includes(file.type) && !isPdf) {
      alert('Unsupported file type. Please upload: JPG, PNG, GIF, WEBP, SVG, BMP, or PDF');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('File is too large. Maximum size is 20MB.');
      return;
    }

    setCompressing(true);
    try {
      let result;
      if (isPdf) {
        result = await renderPdfFirstPage(file);
        setFlyerType('pdf');
      } else {
        result = await compressImage(file);
        setFlyerType('image');
      }

      // Check compressed image size for Firestore (images stored in Firestore)
      if (!isPdf && result.dataUrl.length > 900000) {
        alert('Image is still too large after compression. Please use a smaller image (recommended: under 2MB original).');
        setCompressing(false);
        return;
      }

      setPendingImage(result);
    } catch (err) {
      alert('Error processing file: ' + err.message);
    }
    setCompressing(false);
  };

  const handlePublishFlyer = async () => {
    if (!pendingImage) return;

    if (pendingImage.isPdf) {
      // PDF: store the preview image in Firestore + set pdfUrl for embed
      const pdfFileName = pendingImage.fileName.replace(/[^a-zA-Z0-9.-]/g, '-').toLowerCase();
      const pdfPath = `/images/promo/${pdfFileName}`;

      const confirmed = window.confirm(
        `PDF Flyer Setup:\n\n` +
        `1. Save "${pendingImage.fileName}" to:\n   /public/images/promo/${pdfFileName}\n\n` +
        `2. Push to GitHub (Vercel auto-deploys)\n\n` +
        `3. Click OK to save the promo with the first page as preview.\n\n` +
        `The PDF will display inline on the site once deployed.`
      );
      if (!confirmed) return;

      setPromoSaving(true);
      setPromoSaved(false);
      try {
        const updatedPromo = {
          ...promo,
          imageUrl: pendingImage.dataUrl,
          pdfUrl: pdfPath,
          flyerType: 'pdf',
          pdfPages: pendingImage.totalPages,
          enabled: true,
          updatedAt: new Date().toISOString(),
          updatedBy: user.email,
        };
        await setDoc(doc(db, 'schedule', 'promo'), updatedPromo);
        setPromo(updatedPromo);
        setPendingImage(null);
        setPromoSaved(true);
        setTimeout(() => setPromoSaved(false), 3000);
      } catch (err) {
        alert('Error publishing flyer: ' + err.message);
      }
      setPromoSaving(false);
    } else {
      // Image: store directly in Firestore
      setPromoSaving(true);
      setPromoSaved(false);
      try {
        const updatedPromo = {
          ...promo,
          imageUrl: pendingImage.dataUrl,
          pdfUrl: '',
          flyerType: 'image',
          enabled: true,
          updatedAt: new Date().toISOString(),
          updatedBy: user.email,
        };
        await setDoc(doc(db, 'schedule', 'promo'), updatedPromo);
        setPromo(updatedPromo);
        setPendingImage(null);
        setPromoSaved(true);
        setTimeout(() => setPromoSaved(false), 3000);
      } catch (err) {
        alert('Error publishing flyer: ' + err.message);
      }
      setPromoSaving(false);
    }
  };

  const handleDiscardPending = () => {
    setPendingImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveAnnouncement = async () => {
    setAnnouncementSaving(true);
    setAnnouncementSaved(false);
    try {
      await setDoc(doc(db, 'schedule', 'announcement'), {
        ...announcement,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
      });
      setAnnouncementSaved(true);
      setTimeout(() => setAnnouncementSaved(false), 3000);
    } catch (err) {
      alert('Error saving announcement: ' + err.message);
    }
    setAnnouncementSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await setDoc(doc(db, 'schedule', 'timings'), {
        league: leagueTimings,
        open: openTimings,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
    setSaving(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin/login');
  };

  const updateLeague = (index, field, value) => {
    const updated = [...leagueTimings];
    updated[index] = { ...updated[index], [field]: field === 'enabled' ? !updated[index].enabled : value };
    setLeagueTimings(updated);
  };

  const updateOpen = (index, field, value) => {
    const updated = [...openTimings];
    updated[index] = { ...updated[index], [field]: field === 'enabled' ? !updated[index].enabled : value };
    setOpenTimings(updated);
  };

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!user) return null;

  return (
    <div className="ah-layout">
      <AdminSidebar user={user} onLogout={handleLogout} />

      <div className="ah-main">
      <div className="ah-topbar">
        <div>
          <h1 className="ah-page-title">📅 Schedule Manager</h1>
          <p className="ah-page-sub">Manage league and open bowling timings</p>
        </div>
      </div>

      <div className="admin-content" style={{ maxWidth: '900px' }}>
        <div className="admin-grid">
          {/* League Timings */}
          <div className="admin-card">
            <h3>League Timings</h3>
            <div className="admin-table">
              <div className="admin-table-header">
                <span>Day</span>
                <span>Time</span>
                <span>Active</span>
              </div>
              {leagueTimings.map((item, index) => (
                <div className={`admin-table-row ${!item.enabled ? 'disabled' : ''}`} key={index}>
                  <span className="admin-day">{item.day}</span>
                  <input
                    type="text"
                    value={item.time}
                    onChange={(e) => updateLeague(index, 'time', e.target.value)}
                    placeholder="e.g. 6.30 pm - 9.30 pm"
                    className="admin-input"
                    disabled={!item.enabled}
                  />
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={() => updateLeague(index, 'enabled')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Open Timings */}
          <div className="admin-card">
            <h3>Open Bowling Timings</h3>
            <div className="admin-table">
              <div className="admin-table-header">
                <span>Day</span>
                <span>Time</span>
                <span>Active</span>
              </div>
              {openTimings.map((item, index) => (
                <div className={`admin-table-row ${!item.enabled ? 'disabled' : ''}`} key={index}>
                  <span className="admin-day">{item.day}</span>
                  <div className="admin-input-group">
                    <input
                      type="text"
                      value={item.time}
                      onChange={(e) => updateOpen(index, 'time', e.target.value)}
                      placeholder="e.g. 3.00 pm - 4.30 pm"
                      className="admin-input"
                      disabled={!item.enabled}
                    />
                    <input
                      type="text"
                      value={item.tags || ''}
                      onChange={(e) => updateOpen(index, 'tags', e.target.value)}
                      placeholder="Tags (e.g. Rock & Bowl, Reg Lanes)"
                      className="admin-input admin-input-tags"
                      disabled={!item.enabled}
                    />
                  </div>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={() => updateOpen(index, 'enabled')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="admin-actions">
          <button onClick={handleSave} className="btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
          {saved && <span className="admin-saved">Schedule updated successfully!</span>}
        </div>

        {/* Announcement Banner Manager */}
        <div className="admin-card admin-announcement-card">
          <div className="admin-announcement-header">
            <h3>Announcement Banner</h3>
            <label className="admin-toggle">
              <input
                type="checkbox"
                checked={announcement.enabled}
                onChange={() => setAnnouncement({ ...announcement, enabled: !announcement.enabled })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <p className="admin-announcement-hint">
            {announcement.enabled ? 'Banner is visible on the site during the scheduled window.' : 'Banner is currently hidden from the site.'}
          </p>

          <div className="admin-announcement-fields">
            <div className="admin-field">
              <label className="admin-field-label">Message</label>
              <input
                type="text"
                value={announcement.message}
                onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                placeholder="e.g. Closed Christmas Day — Happy Holidays!"
                className="admin-input"
              />
            </div>

            <div className="admin-field-row">
              <div className="admin-field">
                <label className="admin-field-label">Start Date</label>
                <input
                  type="date"
                  value={announcement.startDate}
                  onChange={(e) => setAnnouncement({ ...announcement, startDate: e.target.value })}
                  className="admin-input"
                />
              </div>
              <div className="admin-field">
                <label className="admin-field-label">Start Time</label>
                <input
                  type="time"
                  value={announcement.startTime}
                  onChange={(e) => setAnnouncement({ ...announcement, startTime: e.target.value })}
                  className="admin-input"
                />
              </div>
            </div>

            <div className="admin-field-row">
              <div className="admin-field">
                <label className="admin-field-label">End Date</label>
                <input
                  type="date"
                  value={announcement.endDate}
                  onChange={(e) => setAnnouncement({ ...announcement, endDate: e.target.value })}
                  className="admin-input"
                />
              </div>
              <div className="admin-field">
                <label className="admin-field-label">End Time</label>
                <input
                  type="time"
                  value={announcement.endTime}
                  onChange={(e) => setAnnouncement({ ...announcement, endTime: e.target.value })}
                  className="admin-input"
                />
              </div>
            </div>
          </div>

          <div className="admin-actions">
            <button onClick={handleSaveAnnouncement} className="btn" disabled={announcementSaving}>
              {announcementSaving ? 'Saving...' : 'Save Announcement'}
            </button>
            {announcementSaved && <span className="admin-saved">Announcement updated!</span>}
          </div>
        </div>

        {/* Promo / Flyer Manager */}
        <div className="admin-card admin-announcement-card">
          <div className="admin-announcement-header">
            <h3>Promo Flyer</h3>
            <label className="admin-toggle">
              <input
                type="checkbox"
                checked={promo.enabled}
                onChange={() => setPromo({ ...promo, enabled: !promo.enabled })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <p className="admin-announcement-hint">
            {promo.enabled ? 'Promo is live on the site.' : 'Promo is currently hidden from the site.'}
          </p>

          <div className="admin-announcement-fields">
            <div className="admin-field">
              <label className="admin-field-label">Title</label>
              <input
                type="text"
                value={promo.title}
                onChange={(e) => setPromo({ ...promo, title: e.target.value })}
                placeholder="e.g. Birthday Party Bash!"
                className="admin-input"
              />
            </div>

            <div className="admin-field">
              <label className="admin-field-label">Description</label>
              <textarea
                value={promo.description}
                onChange={(e) => setPromo({ ...promo, description: e.target.value })}
                placeholder="e.g. Book your kid's birthday party! Includes 2 hours of bowling, shoes, pizza, and drinks. Call now to reserve!"
                className="admin-input admin-textarea"
                rows={3}
              />
            </div>

            <div className="admin-field">
              <label className="admin-field-label">Flyer Image</label>

              {/* Upload zone */}
              <div
                className={`admin-upload-zone ${compressing ? 'uploading' : ''}`}
                onClick={() => !compressing && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/bmp,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    handleFileSelect(e.target.files[0]);
                    e.target.value = '';
                  }}
                />
                {compressing ? (
                  <div className="admin-upload-status">
                    <div className="admin-upload-spinner"></div>
                    <span>Compressing image...</span>
                  </div>
                ) : (
                  <>
                    <div className="admin-upload-icon">📁</div>
                    <p className="admin-upload-text">
                      <strong>Click to browse</strong> and select a flyer
                    </p>
                    <p className="admin-upload-hint">
                      Images (JPG, PNG, GIF, WEBP, SVG, BMP) or PDF — auto-compressed for web
                    </p>
                  </>
                )}
              </div>

              {/* Pending file preview (before publishing) */}
              {pendingImage && (
                <div className="admin-pending-preview">
                  <h4 className="admin-preview-heading">
                    {pendingImage.isPdf ? '📄 PDF Preview (First Page) — Ready to Publish' : 'Preview — Ready to Publish'}
                  </h4>
                  <img src={pendingImage.dataUrl} alt="Pending flyer" className="admin-promo-preview-large" />
                  <div className="admin-preview-info">
                    <span>📄 {pendingImage.fileName}</span>
                    <span>📐 {pendingImage.width} × {pendingImage.height}px</span>
                    <span>📦 {formatBytes(pendingImage.originalSize)} → {formatBytes(pendingImage.compressedSize)}</span>
                    {pendingImage.isPdf && <span>📑 {pendingImage.totalPages} page{pendingImage.totalPages > 1 ? 's' : ''}</span>}
                  </div>
                  {pendingImage.isPdf && (
                    <div className="admin-pdf-note">
                      <strong>PDF Setup:</strong> After publishing, save the PDF file to <code>/public/images/promo/</code> and push to GitHub.
                      The first page preview will show immediately, and the full PDF will display inline once deployed.
                    </div>
                  )}
                  <div className="admin-preview-actions">
                    <button onClick={handlePublishFlyer} className="btn" disabled={promoSaving}>
                      {promoSaving ? 'Publishing...' : '✓ Publish Flyer & Enable Promo'}
                    </button>
                    <button onClick={handleDiscardPending} className="admin-remove-image" type="button">
                      ✕ Discard
                    </button>
                  </div>
                </div>
              )}

              {/* Current active image */}
              {!pendingImage && promo.imageUrl && (
                <div className="admin-image-preview-wrap">
                  <h4 style={{ color: '#2AD5FF', marginBottom: '8px' }}>Current Flyer</h4>
                  <img src={convertImageUrl(promo.imageUrl)} alt="Current flyer" className="admin-promo-preview" />
                  <button className="admin-remove-image" onClick={handleRemoveImage} type="button">
                    ✕ Remove Image
                  </button>
                </div>
              )}
            </div>

            <div className="admin-field">
              <label className="admin-field-label">Display Options</label>
              <div className="admin-checkbox-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={promo.showFloating}
                    onChange={() => setPromo({ ...promo, showFloating: !promo.showFloating })}
                  />
                  <span>Floating card (bottom-right corner)</span>
                </label>
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={promo.showSection}
                    onChange={() => setPromo({ ...promo, showSection: !promo.showSection })}
                  />
                  <span>Full-width section on homepage</span>
                </label>
              </div>
            </div>
          </div>

          <div className="admin-actions">
            <button onClick={handleSavePromo} className="btn" disabled={promoSaving}>
              {promoSaving ? 'Saving...' : 'Save Promo Settings'}
            </button>
            {promoSaved && <span className="admin-saved">Promo updated!</span>}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default AdminPanel;
